/**
 * Workflow Builder rules shared by the server, the web builder and native clients (contracts workflows.ts,
 * docs/features/workflows.md): validation, the variables each step can use, template resolution, conditions, form
 * modals and answers, schedules, webhook inputs and who may manage a workflow.
 *
 * - **Variables** are referenced as `{{trigger.user}}` / `{{steps.s2.answer}}`. A step can use the trigger's
 *   variables and the outputs of steps before it (`availableVariables`).
 * - **Templates** (`resolveTemplate`): users become `<@id>` and channels `<#id>` in message text (mentions); `plain`
 *   leaves ids as they are (conditions, inputs). A missing variable is empty, like Slack.
 * - **Conditions** compare case-insensitively after trimming; `greater_than` / `less_than` compare numbers only.
 * - **Schedules** keep the start's wall-clock time in its zone across DST (`nextScheduledRun`).
 */
import type {
  FormField,
  KitBlock,
  KitStateValues,
  KitView,
  Workflow,
  WorkflowCondition,
  WorkflowDefinition,
  WorkflowSchedule,
  WorkflowStep,
  WorkflowTrigger,
  WorkflowValue,
  WorkflowVarType,
} from "@bundle/contracts";
import { MAX_WORKFLOW_STEPS } from "@bundle/contracts";
import { fromLocal, localParts } from "./calendar.ts";

/** A variable a step can insert: `ref` goes between `{{ }}`; `label` is the chip's text (`source` › `name`). */
export type WorkflowVariable = { ref: string; type: WorkflowVarType; source: string; name: string };

export type WorkflowProblem =
  | { code: "noName" }
  | { code: "noSteps" }
  | { code: "tooManySteps" }
  | { code: "noChannel" }
  | { code: "noEmoji" }
  | { code: "badTimeZone" }
  | { code: "scheduleInPast" }
  | { code: "duplicateVariable"; key: string }
  | { code: "duplicateStep"; stepId: string }
  | { code: "emptyText"; stepId: string }
  | { code: "unknownVariable"; stepId: string; ref: string }
  | { code: "noRunner"; stepId: string }
  | { code: "formNeedsLink"; stepId: string }
  | { code: "duplicateField"; stepId: string; fieldId: string }
  | { code: "noOptions"; stepId: string; fieldId: string }
  | { code: "notAMessage"; stepId: string }
  | { code: "notAUser"; stepId: string }
  | { code: "noDatabase"; stepId: string }
  | { code: "noApp"; stepId: string };

/** The `{{ref}}` pattern (refs are dotted lowercase words). */
const REF = /\{\{\s*([a-z][a-z0-9_]*(?:\.[a-z0-9_]+)*)\s*\}\}/g;

/** The variable refs a template uses, in order, each once. */
export function templateRefs(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(REF)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

/** A template as text and variable pieces (the builder draws variables as chips). */
export function templateSegments(text: string): ({ kind: "text"; text: string } | { kind: "variable"; ref: string })[] {
  const out: ({ kind: "text"; text: string } | { kind: "variable"; ref: string })[] = [];
  let at = 0;
  for (const m of text.matchAll(REF)) {
    if (m.index > at) out.push({ kind: "text", text: text.slice(at, m.index) });
    out.push({ kind: "variable", ref: m[1]! });
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push({ kind: "text", text: text.slice(at) });
  return out;
}

/** What a ref looks like in a template (what the builder inserts). */
export function variableToken(ref: string): string {
  return `{{${ref}}}`;
}

/** The trigger's variables (Slack: "Person who used this workflow", "Channel where the workflow was used", …). */
export function triggerVariables(trigger: WorkflowTrigger): WorkflowVariable[] {
  const v = (name: string, type: WorkflowVarType): WorkflowVariable => ({ ref: `trigger.${name}`, type, source: "trigger", name });
  switch (trigger.type) {
    case "link":
    case "member_joined":
      return [v("user", "user"), v("channel", "channel"), v("time", "time")];
    case "reaction":
      return [v("user", "user"), v("channel", "channel"), v("message", "message"), v("message_text", "text"), v("message_author", "user"), v("emoji", "text"), v("time", "time")];
    case "schedule":
      return [v("time", "time")];
    case "webhook":
      return [...trigger.variables.map((x) => v(x.key, x.type)), v("time", "time")];
  }
}

const FIELD_TYPE: Record<FormField["kind"], WorkflowVarType> = { text: "text", paragraph: "text", number: "number", select: "text", user: "user", channel: "channel", date: "date" };

/** What a step gives later steps. */
export function stepOutputs(step: WorkflowStep): WorkflowVariable[] {
  const v = (name: string, type: WorkflowVarType): WorkflowVariable => ({ ref: `steps.${step.id}.${name}`, type, source: step.id, name });
  switch (step.type) {
    case "send_message":
      return [v("message", "message"), v("channel", "channel")];
    case "form":
      return [v("user", "user"), ...step.fields.map((f) => v(f.id, FIELD_TYPE[f.kind]))];
    case "create_page":
      return [v("page", "page")];
    case "create_row":
      return [v("row", "row")];
    case "app_step":
      return step.outputs.map((o) => v(o.name, o.type));
    case "add_reaction":
    case "add_to_channel":
    case "delay":
      return [];
  }
}

/** Variables a step can use: the trigger's and earlier steps' outputs (`stepId` absent: after the last step). */
export function availableVariables(def: Pick<WorkflowDefinition, "trigger" | "steps">, stepId?: string): WorkflowVariable[] {
  const out = triggerVariables(def.trigger);
  for (const s of def.steps) {
    if (s.id === stepId) break;
    out.push(...stepOutputs(s));
  }
  return out;
}

/** The person who ran the workflow (link), joined (member_joined) or reacted (reaction). */
export function hasRunner(trigger: WorkflowTrigger): boolean {
  return trigger.type === "link" || trigger.type === "member_joined" || trigger.type === "reaction";
}

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** Templates a step reads, for checking their refs. */
function stepTemplates(step: WorkflowStep): string[] {
  const out: string[] = [];
  if (step.condition !== undefined) out.push(step.condition.value, step.condition.compare ?? "");
  switch (step.type) {
    case "send_message":
      out.push(step.text);
      if (step.to.kind === "variable") out.push(variableToken(step.to.ref));
      break;
    case "add_reaction":
      out.push(step.message);
      break;
    case "create_page":
      out.push(step.title, step.text ?? "");
      break;
    case "create_row":
      out.push(step.title, ...Object.values(step.values ?? {}));
      break;
    case "add_to_channel":
      out.push(step.user);
      break;
    case "app_step":
      out.push(...Object.values(step.inputs));
      break;
    case "form":
    case "delay":
      break;
  }
  return out;
}

/** A template that is exactly one variable → its ref. */
function soleRef(text: string): string | undefined {
  const refs = templateRefs(text);
  return refs.length === 1 && text.trim() === variableToken(refs[0]!) ? refs[0] : undefined;
}

/**
 * What stops a definition from being published (empty = fine). `now` checks a one-time schedule isn't in the past.
 * Variables must exist before the step that uses them; forms need a link trigger (someone to show them to).
 */
export function workflowProblems(def: WorkflowDefinition, now: number): WorkflowProblem[] {
  const out: WorkflowProblem[] = [];
  if (def.name.trim() === "") out.push({ code: "noName" });
  const t = def.trigger;
  if ((t.type === "link" || t.type === "member_joined" || t.type === "reaction") && t.channelId.trim() === "") out.push({ code: "noChannel" });
  if (t.type === "reaction" && t.emoji.trim() === "") out.push({ code: "noEmoji" });
  if (t.type === "schedule") {
    if (!isValidZone(t.schedule.timeZone)) out.push({ code: "badTimeZone" });
    else if (t.schedule.repeat === "none" && t.schedule.startAt <= now) out.push({ code: "scheduleInPast" });
  }
  if (t.type === "webhook") {
    const seen = new Set<string>();
    for (const v of t.variables) {
      if (seen.has(v.key)) out.push({ code: "duplicateVariable", key: v.key });
      seen.add(v.key);
    }
  }
  if (def.steps.length === 0) out.push({ code: "noSteps" });
  if (def.steps.length > MAX_WORKFLOW_STEPS) out.push({ code: "tooManySteps" });
  const ids = new Set<string>();
  for (const s of def.steps) {
    if (ids.has(s.id)) out.push({ code: "duplicateStep", stepId: s.id });
    ids.add(s.id);
    const vars = availableVariables(def, s.id);
    const known = new Map(vars.map((v) => [v.ref, v.type]));
    for (const text of stepTemplates(s)) {
      for (const ref of templateRefs(text)) if (!known.has(ref)) out.push({ code: "unknownVariable", stepId: s.id, ref });
    }
    switch (s.type) {
      case "send_message":
        if (s.text.trim() === "") out.push({ code: "emptyText", stepId: s.id });
        if (s.to.kind === "runner" && !hasRunner(t)) out.push({ code: "noRunner", stepId: s.id });
        if (s.to.kind === "channel" && s.to.channelId.trim() === "") out.push({ code: "noChannel" });
        if (s.to.kind === "variable" && known.has(s.to.ref) && known.get(s.to.ref) !== "user" && known.get(s.to.ref) !== "channel") out.push({ code: "notAUser", stepId: s.id });
        break;
      case "form": {
        if (t.type !== "link") out.push({ code: "formNeedsLink", stepId: s.id });
        const fields = new Set<string>();
        for (const f of s.fields) {
          if (fields.has(f.id)) out.push({ code: "duplicateField", stepId: s.id, fieldId: f.id });
          fields.add(f.id);
          if (f.kind === "select" && (f.options ?? []).length === 0) out.push({ code: "noOptions", stepId: s.id, fieldId: f.id });
        }
        break;
      }
      case "add_reaction": {
        const ref = soleRef(s.message);
        if (ref === undefined || (known.has(ref) && known.get(ref) !== "message")) out.push({ code: "notAMessage", stepId: s.id });
        if (s.emoji.trim() === "") out.push({ code: "noEmoji" });
        break;
      }
      case "add_to_channel": {
        const ref = soleRef(s.user);
        if (ref !== undefined && known.has(ref) && known.get(ref) !== "user") out.push({ code: "notAUser", stepId: s.id });
        if (s.channelId.trim() === "") out.push({ code: "noChannel" });
        break;
      }
      case "create_row":
        if (s.databaseId.trim() === "") out.push({ code: "noDatabase", stepId: s.id });
        break;
      case "app_step":
        if (s.appId.trim() === "") out.push({ code: "noApp", stepId: s.id });
        break;
      case "create_page":
      case "delay":
        break;
    }
  }
  return out;
}

/** A variable's value in text: users and channels as mentions unless `plain`. */
function show(v: WorkflowValue | undefined, plain: boolean): string {
  if (v === undefined) return "";
  if (plain) return v.value;
  if (v.type === "user" && v.value !== "") return `<@${v.value}>`;
  if (v.type === "channel" && v.value !== "") return `<#${v.value}>`;
  return v.value;
}

/** Fills a template's variables (missing ones are empty). */
export function resolveTemplate(text: string, vars: Readonly<Record<string, WorkflowValue>>, plain = false): string {
  return text.replace(REF, (_m, ref: string) => show(vars[ref], plain));
}

/** A template that is one variable → that value (its type kept); otherwise the plain text as `text`. */
export function resolveValue(text: string, vars: Readonly<Record<string, WorkflowValue>>): WorkflowValue {
  const ref = soleRef(text);
  if (ref !== undefined) return vars[ref] ?? { type: "text", value: "" };
  return { type: "text", value: resolveTemplate(text, vars, true) };
}

const fold = (s: string) => s.trim().toLowerCase();

/** Whether a step's condition holds (no condition: always). */
export function evaluateCondition(c: WorkflowCondition | undefined, vars: Readonly<Record<string, WorkflowValue>>): boolean {
  if (c === undefined) return true;
  const left = fold(resolveTemplate(c.value, vars, true));
  const right = fold(resolveTemplate(c.compare ?? "", vars, true));
  switch (c.op) {
    case "is":
      return left === right;
    case "is_not":
      return left !== right;
    case "contains":
      return right !== "" && left.includes(right);
    case "is_empty":
      return left === "";
    case "is_not_empty":
      return left !== "";
    case "greater_than":
    case "less_than": {
      if (left === "" || right === "") return false;
      const a = Number(left);
      const b = Number(right);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
      return c.op === "greater_than" ? a > b : a < b;
    }
  }
}

/** The modal a form step shows (input blocks; block id = field id, action id "value"). */
export function formView(step: Extract<WorkflowStep, { type: "form" }>, metadata: string): KitView {
  const blocks: KitBlock[] = step.fields.map((f): KitBlock => {
    const label = { type: "plain_text" as const, text: f.label };
    const base = { type: "input" as const, block_id: f.id, label, ...(f.required ? {} : { optional: true }) };
    switch (f.kind) {
      case "text":
        return { ...base, element: { type: "plain_text_input", action_id: "value", max_length: 3000 } };
      case "paragraph":
        return { ...base, element: { type: "plain_text_input", action_id: "value", multiline: true, max_length: 3000 } };
      case "number":
        return { ...base, element: { type: "plain_text_input", action_id: "value", max_length: 30 } };
      case "select":
        return { ...base, element: { type: "static_select", action_id: "value", options: (f.options ?? []).map((o) => ({ text: { type: "plain_text", text: o }, value: o })) } };
      case "user":
        return { ...base, element: { type: "users_select", action_id: "value" } };
      case "channel":
        return { ...base, element: { type: "conversations_select", action_id: "value", default_to_current_conversation: true } };
      case "date":
        return { ...base, element: { type: "datepicker", action_id: "value" } };
    }
  });
  return {
    type: "modal",
    title: { type: "plain_text", text: step.title },
    submit: { type: "plain_text", text: step.submitLabel !== undefined && step.submitLabel.trim() !== "" ? step.submitLabel : "Submit" },
    blocks,
    callback_id: "workflow_form",
    private_metadata: metadata,
  };
}

/** A submitted form's answers by field id, or the errors by block id (a required answer missing, a number that isn't one). */
export function formAnswers(step: Extract<WorkflowStep, { type: "form" }>, state: KitStateValues): { answers: Record<string, WorkflowValue> } | { errors: Record<string, string> } {
  const answers: Record<string, WorkflowValue> = {};
  const errors: Record<string, string> = {};
  for (const f of step.fields) {
    const v = state[f.id]?.value;
    let value = "";
    if (v !== undefined) {
      if (f.kind === "select") value = v.selected_option?.value ?? "";
      else if (f.kind === "user") value = v.selected_user ?? "";
      else if (f.kind === "channel") value = v.selected_conversation ?? v.selected_channel ?? "";
      else if (f.kind === "date") value = v.selected_date ?? "";
      else value = (v.value ?? "").trim();
    }
    if (f.kind === "select" && value !== "" && !(f.options ?? []).includes(value)) value = "";
    if (value === "" && f.required) errors[f.id] = "Please complete this required field.";
    else if (f.kind === "number" && value !== "" && !Number.isFinite(Number(value))) errors[f.id] = "Enter a number.";
    answers[f.id] = { type: FIELD_TYPE[f.kind], value };
  }
  return Object.keys(errors).length > 0 ? { errors } : { answers };
}

const MIN = 60_000;

/**
 * The first run of a schedule at or after `after` (null: a one-time schedule that has passed). Repeats keep the
 * start's wall-clock time in its zone; monthly runs on the start's day (the month's last day when shorter).
 */
export function nextScheduledRun(s: WorkflowSchedule, after: number): number | null {
  if (s.repeat === "none") return s.startAt >= after ? s.startAt : null;
  const start = localParts(s.startAt, s.timeZone);
  // Seconds and milliseconds of the start carry over to every run.
  const sub = s.startAt - fromLocal(start.year, start.month, start.day, start.hour, start.minute, s.timeZone);
  const from = Math.max(after, s.startAt);
  const cur = localParts(from, s.timeZone);
  const days = s.repeat === "weekly" ? (s.days !== undefined && s.days.length > 0 ? s.days : [start.weekday]) : [];
  for (let i = 0; i <= 400; i += 1) {
    const d = new Date(Date.UTC(cur.year, cur.month - 1, cur.day + i));
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const wd = d.getUTCDay();
    let ok = false;
    switch (s.repeat) {
      case "daily":
        ok = true;
        break;
      case "weekdays":
        ok = wd >= 1 && wd <= 5;
        break;
      case "weekly":
        ok = days.includes(wd);
        break;
      case "monthly": {
        const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
        ok = day === Math.min(start.day, last);
        break;
      }
    }
    if (!ok) continue;
    const at = fromLocal(y, m, day, start.hour, start.minute, s.timeZone) + sub;
    if (at >= from && at >= s.startAt) return at;
  }
  return null;
}

/** A webhook body → the trigger's variables (all declared keys must be strings; extra keys are ignored), or the bad key. */
export function webhookInputs(trigger: Extract<WorkflowTrigger, { type: "webhook" }>, body: unknown): { vars: Record<string, WorkflowValue> } | { missing: string } {
  const obj = typeof body === "object" && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : {};
  const vars: Record<string, WorkflowValue> = {};
  for (const v of trigger.variables) {
    const raw = obj[v.key];
    const value = typeof raw === "string" ? raw : typeof raw === "number" || typeof raw === "boolean" ? String(raw) : undefined;
    if (value === undefined) return { missing: v.key };
    vars[`trigger.${v.key}`] = { type: v.type, value: value.slice(0, 4000) };
  }
  return { vars };
}

/** Who may create workflows: members (not guests or agents), only owners/admins when the workspace says so. */
export function canCreateWorkflows(me: { role: string; restricted?: boolean | undefined; kind?: string | undefined }, creators: "everyone" | "admins"): boolean {
  if (me.restricted === true || me.role === "guest" || me.kind === "agent") return false;
  if (creators === "admins") return me.role === "owner" || me.role === "admin";
  return true;
}

/** Who may edit, publish, unpublish, delete and see the activity log: the creator, its managers, owners and admins. */
export function canManageWorkflow(me: { userId: string; role: string; restricted?: boolean | undefined }, wf: Pick<Workflow, "createdBy" | "managers">): boolean {
  if (me.restricted === true || me.role === "guest") return false;
  return me.role === "owner" || me.role === "admin" || wf.createdBy === me.userId || wf.managers.includes(me.userId);
}

/** Published link workflows in a channel (its workflows menu and "/" menu), by name. */
export function channelWorkflows(list: readonly Workflow[], channelId: string): Workflow[] {
  return list.filter((w) => w.published && w.definition.trigger.type === "link" && w.definition.trigger.channelId === channelId).toSorted((a, b) => a.definition.name.localeCompare(b.definition.name));
}

/** A new step id not used by `steps` ("s1", "s2", …). */
export function newStepId(steps: readonly { id: string }[]): string {
  for (let i = 1; ; i += 1) {
    const id = `s${i}`;
    if (!steps.some((s) => s.id === id)) return id;
  }
}

/** A blank step of a type (the builder's "Add step"). */
export function blankStep(type: WorkflowStep["type"], id: string, trigger: WorkflowTrigger): WorkflowStep {
  switch (type) {
    case "send_message":
      return { type, id, to: hasRunner(trigger) ? { kind: "runner" } : { kind: "channel", channelId: "channelId" in trigger ? trigger.channelId : "" }, text: "" };
    case "form":
      return { type, id, title: "Form", fields: [{ id: "answer", label: "Question", kind: "text", required: true }] };
    case "add_reaction":
      return { type, id, message: trigger.type === "reaction" ? variableToken("trigger.message") : "", emoji: "✅" };
    case "create_page":
      return { type, id, title: "" };
    case "create_row":
      return { type, id, databaseId: "", title: "" };
    case "add_to_channel":
      return { type, id, channelId: "", user: hasRunner(trigger) ? variableToken("trigger.user") : "" };
    case "delay":
      return { type, id, minutes: 60 };
    case "app_step":
      return { type, id, appId: "", callbackId: "", title: "", inputs: {}, outputs: [] };
  }
}

/** Moves the step at `from` to `to` (both in range). Variables used before they exist show up in `workflowProblems`. */
export function moveStep(steps: readonly WorkflowStep[], from: number, to: number): WorkflowStep[] {
  const out = [...steps];
  const [s] = out.splice(from, 1);
  if (s !== undefined) out.splice(Math.max(0, Math.min(to, out.length)), 0, s);
  return out;
}

/** The run's starting variables for a trigger (`time` added here). */
export function triggerValues(trigger: WorkflowTrigger, given: Readonly<Record<string, string>>, now: number): Record<string, WorkflowValue> {
  const out: Record<string, WorkflowValue> = {};
  for (const v of triggerVariables(trigger)) {
    if (v.name === "time") out[v.ref] = { type: "time", value: new Date(now).toISOString() };
    else if (given[v.name] !== undefined) out[v.ref] = { type: v.type, value: given[v.name]! };
  }
  return out;
}

/** Minutes → the wait as Slack words it ("1 hour", "2 days", "90 minutes"). */
export function delayParts(minutes: number): { unit: "minutes" | "hours" | "days"; count: number } {
  if (minutes % (24 * 60) === 0) return { unit: "days", count: minutes / (24 * 60) };
  if (minutes % 60 === 0) return { unit: "hours", count: minutes / 60 };
  return { unit: "minutes", count: minutes };
}

/** Exported for the delay step (ms). */
export function delayMs(minutes: number): number {
  return minutes * MIN;
}
